/**
 * The design Edit dialog's **Screen print** tab (docs/screen-print-station-audit-and-plan-v1.md § 3.1,
 * § 3.6, phase P2): the technical sheet the screen room reads, typed once per design. Colours in print
 * order with head · Pantone + hex · ink/mix · mesh · frame · flash; the press it was planned on; cure;
 * press notes — and the carousel redrawn live as heads are typed (warnings, never refusals).
 *
 * Saves ONLY `screenPrintSpec` through `PATCH /designs/:id` (name and code have their own tab); the
 * server stamps who/when, so nothing here sends `setAt`/`setBy`. Clear sends `null`. No money path.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Loader2, Plus, Trash2, TriangleAlert } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useApiClient } from '@/lib/api/hooks';
import type { DesignSummary } from './design-lib';
import { PressCarousel } from './press-carousel';
import {
  draftErrors,
  draftsFromSpec,
  FRAME_CHOICES,
  isValidHex,
  MESH_CHOICES,
  newDraft,
  PRESS_MAX_STATIONS,
  type PressShape,
  type PressSummary,
  ringScreensFromDrafts,
  type ScreenDraft,
  specBoxFromDrafts,
  stationLayout,
} from './screen-print-spec-lib';

/** A press to draw against when none is registered yet — the shop's M&R shape (D17). */
const FALLBACK_PRESS: PressShape = {
  kind: 'automatic',
  stations: 12,
  printHeads: 10,
  flashStations: [2, 8],
};

function usePressList() {
  const client = useApiClient();
  return useQuery({
    queryKey: ['presses'],
    queryFn: async () => {
      const { data, error } = await client.GET('/presses');
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load presses.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data.items as PressSummary[];
    },
  });
}

export function DesignSpecEditor({
  design,
  onSaved,
}: {
  design: DesignSummary;
  /** Called after a successful save or clear — the dialog closes and the library refetches. */
  onSaved: () => void;
}) {
  const client = useApiClient();
  const qc = useQueryClient();
  const spec = design.screenPrintSpec;
  const presses = usePressList();

  const [drafts, setDrafts] = useState<ScreenDraft[]>(() => draftsFromSpec(spec));
  const [cureTempF, setCureTempF] = useState(spec?.cureTempF != null ? String(spec.cureTempF) : '');
  const [cureSeconds, setCureSeconds] = useState(
    spec?.cureSeconds != null ? String(spec.cureSeconds) : '',
  );
  const [pressNotes, setPressNotes] = useState(spec?.pressNotes ?? '');
  const [pressId, setPressId] = useState<string | undefined>(spec?.pressId);
  const [confirmClear, setConfirmClear] = useState(false);

  // The press the heads refer to: the spec's own, else the shop default, else the first registered,
  // else the M&R shape — so the ring always has something honest to draw against.
  const press: PressShape & { pressId?: string; name?: string } = useMemo(() => {
    const list = presses.data ?? [];
    const chosen =
      list.find((p) => p.pressId === pressId) ?? list.find((p) => p.isDefault) ?? list[0];
    return chosen ?? FALLBACK_PRESS;
  }, [presses.data, pressId]);

  // Once the list arrives, pin the picker to the press actually being drawn (so the saved spec names it).
  useEffect(() => {
    if (!pressId && 'pressId' in press && press.pressId) setPressId(press.pressId);
  }, [press, pressId]);

  const errors = draftErrors(drafts, press.stations);
  const layout = stationLayout(ringScreensFromDrafts(drafts), press);
  const box = specBoxFromDrafts(
    drafts,
    { cureTempF, cureSeconds, pressNotes, pressId },
    press.stations,
  );

  const save = useMutation({
    mutationFn: async () => {
      if (!box) throw new Error(errors[0] ?? 'Check the colours.');
      const res = await client.PATCH('/designs/{designId}', {
        params: { path: { designId: design.designId } },
        body: { screenPrintSpec: box },
      });
      if (res.error || !res.data?.ok) {
        throw new Error(res.error?.error?.detail ?? 'Save failed.');
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['designs'] });
      onSaved();
    },
  });

  const clear = useMutation({
    mutationFn: async () => {
      const res = await client.PATCH('/designs/{designId}', {
        params: { path: { designId: design.designId } },
        body: { screenPrintSpec: null },
      });
      if (res.error || !res.data?.ok) {
        throw new Error(res.error?.error?.detail ?? 'Clear failed.');
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['designs'] });
      onSaved();
    },
  });

  const update = (key: string, patch: Partial<ScreenDraft>) =>
    setDrafts((ds) => ds.map((d) => (d.key === key ? { ...d, ...patch } : d)));
  const remove = (key: string) => setDrafts((ds) => ds.filter((d) => d.key !== key));
  const move = (key: string, dir: -1 | 1) =>
    setDrafts((ds) => {
      const i = ds.findIndex((d) => d.key === key);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= ds.length) return ds;
      const next = [...ds];
      const [row] = next.splice(i, 1);
      if (row) next.splice(j, 0, row);
      return next;
    });

  const busy = save.isPending || clear.isPending;
  const pressList = presses.data ?? [];

  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_260px]">
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-medium">
              Colours · in print order{' '}
              <span className="font-normal text-muted-foreground">
                (the row order is the print order; the head is the station on the press)
              </span>
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDrafts((ds) => [...ds, newDraft()])}
              disabled={drafts.length >= PRESS_MAX_STATIONS}
            >
              <Plus className="size-4" /> Add colour
            </Button>
          </div>

          <ol className="space-y-3">
            {drafts.map((d, i) => (
              <li key={d.key} className="rounded-md border p-3" data-testid="screen-row">
                <div className="grid grid-cols-[28px_64px_minmax(0,1fr)_minmax(0,1fr)_auto] items-end gap-2">
                  <div className="pb-2 text-center text-sm font-bold tabular-nums">{i + 1}</div>
                  <div className="space-y-1">
                    <Label htmlFor={`${d.key}-head`} className="text-xs">
                      Head
                    </Label>
                    <Input
                      id={`${d.key}-head`}
                      type="number"
                      inputMode="numeric"
                      min="1"
                      max={press.stations}
                      value={d.head}
                      onChange={(e) => update(d.key, { head: e.target.value })}
                      aria-label={`Head for colour ${i + 1}`}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor={`${d.key}-name`} className="text-xs">
                      Colour
                    </Label>
                    <Input
                      id={`${d.key}-name`}
                      value={d.name}
                      onChange={(e) => update(d.key, { name: e.target.value })}
                      placeholder={i === 0 ? 'Underbase' : 'Glow green'}
                      aria-label={`Name for colour ${i + 1}`}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor={`${d.key}-pantone`} className="text-xs">
                      Pantone
                    </Label>
                    <div className="flex items-center gap-1.5">
                      <input
                        type="color"
                        aria-label={`Ink colour for colour ${i + 1}`}
                        className="size-8 shrink-0 cursor-pointer rounded border bg-transparent p-0"
                        value={isValidHex(d.hex) ? d.hex : '#888888'}
                        onChange={(e) => update(d.key, { hex: e.target.value.toUpperCase() })}
                      />
                      <Input
                        id={`${d.key}-pantone`}
                        value={d.pantone}
                        onChange={(e) => update(d.key, { pantone: e.target.value })}
                        placeholder="PMS 802 C"
                        aria-label={`Pantone for colour ${i + 1}`}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 pb-0.5">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => move(d.key, -1)}
                      disabled={i === 0}
                      aria-label={`Move colour ${i + 1} up`}
                    >
                      <ArrowUp className="size-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => move(d.key, 1)}
                      disabled={i === drafts.length - 1}
                      aria-label={`Move colour ${i + 1} down`}
                    >
                      <ArrowDown className="size-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive"
                      onClick={() => remove(d.key)}
                      aria-label={`Remove colour ${i + 1}`}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                </div>
                {/* Stacked, not a three-track grid: the mesh + frame clusters are ~680 px of rigid
                    `auto` tracks inside a ~540 px card, which starved the ink field to nothing and
                    pushed the frame input over the carousel (seen in prod 2026-09-18). */}
                <div className="mt-2 space-y-2">
                  <Input
                    value={d.ink}
                    onChange={(e) => update(d.key, { ink: e.target.value })}
                    placeholder="Ink · mix — Wilflex Epic Glow, 3:1 with Glow Base, 2 strokes"
                    aria-label={`Ink and mix for colour ${i + 1}`}
                  />
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-muted-foreground">mesh</span>
                      <ToggleGroup
                        type="single"
                        variant="outline"
                        size="sm"
                        value={
                          MESH_CHOICES.some((m) => String(m) === d.meshCount) ? d.meshCount : ''
                        }
                        onValueChange={(v) => v && update(d.key, { meshCount: v })}
                        aria-label={`Mesh for colour ${i + 1}`}
                      >
                        {MESH_CHOICES.map((m) => (
                          <ToggleGroupItem key={m} value={String(m)}>
                            {m}
                          </ToggleGroupItem>
                        ))}
                      </ToggleGroup>
                      <Input
                        type="number"
                        inputMode="numeric"
                        className="w-20"
                        value={d.meshCount}
                        onChange={(e) => update(d.key, { meshCount: e.target.value })}
                        placeholder="other"
                        aria-label={`Mesh count for colour ${i + 1}`}
                      />
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-muted-foreground">frame</span>
                      <ToggleGroup
                        type="single"
                        variant="outline"
                        size="sm"
                        value={FRAME_CHOICES.some((f) => f === d.frame) ? d.frame : ''}
                        onValueChange={(v) => v && update(d.key, { frame: v })}
                        aria-label={`Frame for colour ${i + 1}`}
                      >
                        {FRAME_CHOICES.map((f) => (
                          <ToggleGroupItem key={f} value={f}>
                            {f}
                          </ToggleGroupItem>
                        ))}
                      </ToggleGroup>
                      <Input
                        className="w-24"
                        value={d.frame}
                        onChange={(e) => update(d.key, { frame: e.target.value })}
                        placeholder="other"
                        aria-label={`Frame for colour ${i + 1}, free text`}
                      />
                    </div>
                  </div>
                </div>
                {press.kind === 'manual' && (
                  <div className="mt-2 flex items-center gap-2 text-sm">
                    <Checkbox
                      id={`${d.key}-flash`}
                      checked={d.flashAfter}
                      onCheckedChange={(v) => update(d.key, { flashAfter: v === true })}
                    />
                    <Label htmlFor={`${d.key}-flash`} className="font-normal">
                      flash-cure after this colour
                    </Label>
                  </div>
                )}
              </li>
            ))}
          </ol>

          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1">
              <Label htmlFor="spec-cure-temp" className="text-xs">
                Cure °F
              </Label>
              <Input
                id="spec-cure-temp"
                type="number"
                inputMode="numeric"
                value={cureTempF}
                onChange={(e) => setCureTempF(e.target.value)}
                placeholder="320"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="spec-cure-seconds" className="text-xs">
                Cure dwell (s)
              </Label>
              <Input
                id="spec-cure-seconds"
                type="number"
                inputMode="numeric"
                value={cureSeconds}
                onChange={(e) => setCureSeconds(e.target.value)}
                placeholder="60"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="spec-press" className="text-xs">
                Press the heads refer to
              </Label>
              <select
                id="spec-press"
                className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                value={pressId ?? ''}
                onChange={(e) => setPressId(e.target.value || undefined)}
                disabled={pressList.length === 0}
              >
                {pressList.length === 0 && (
                  <option value="">No press registered — M&R 12/10 shape</option>
                )}
                {pressList.map((p) => (
                  <option key={p.pressId} value={p.pressId}>
                    {p.name}
                    {p.isDefault ? ' (default)' : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="spec-notes" className="text-xs">
              Press notes
            </Label>
            <Textarea
              id="spec-notes"
              value={pressNotes}
              onChange={(e) => setPressNotes(e.target.value)}
              placeholder="Off-contact 1/16 in · 70-duro squeegee · flash 3 s between colours"
              rows={2}
            />
          </div>
        </div>

        <div className="space-y-2">
          <PressCarousel
            press={press}
            screens={ringScreensFromDrafts(drafts)}
            caption={[
              'name' in press && press.name ? press.name : 'M&R 12/10',
              'head order = print order',
            ]}
            className="mx-auto w-full max-w-[260px]"
          />
          <p className="text-center text-xs text-muted-foreground">
            {press.stations} stations · {press.printHeads} heads
            {press.flashStations.length > 0 ? ` · flash at ${press.flashStations.join(', ')}` : ''}
          </p>
          {layout.warnings.length > 0 && (
            <ul className="space-y-1" aria-label="Press layout warnings">
              {layout.warnings.map((w) => (
                <li
                  key={w}
                  className="flex items-start gap-1.5 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-800"
                >
                  <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
                  {w}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {errors.length > 0 && drafts.some((d) => d.name.trim() || d.head || d.hex) && (
        <ul className="space-y-0.5 text-sm text-destructive" aria-label="Spec errors">
          {errors.map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      )}
      {(save.isError || clear.isError) && (
        <Alert variant="destructive">
          <AlertDescription>
            {(save.error ?? clear.error) instanceof Error
              ? ((save.error ?? clear.error) as Error).message
              : 'Save failed.'}
          </AlertDescription>
        </Alert>
      )}
      {spec && (
        <p className="text-xs text-muted-foreground">
          Spec set {new Date(spec.setAt).toLocaleString()}
          {spec.setBy ? ` · ${spec.setBy}` : ''}
        </p>
      )}

      <DialogFooter className="sm:justify-between">
        {spec ? (
          confirmClear ? (
            <Button
              variant="destructive"
              onClick={() => clear.mutate()}
              disabled={busy}
              aria-label="Confirm clear spec"
            >
              {clear.isPending && <Loader2 className="size-4 animate-spin" />}
              Really clear the spec
            </Button>
          ) : (
            <Button
              variant="ghost"
              className="text-destructive"
              onClick={() => setConfirmClear(true)}
            >
              Clear spec
            </Button>
          )
        ) : (
          <span />
        )}
        <Button onClick={() => save.mutate()} disabled={!box || busy}>
          {save.isPending && <Loader2 className="size-4 animate-spin" />}
          Save spec
        </Button>
      </DialogFooter>
    </div>
  );
}
